import React, { useMemo, useRef } from 'react';
import { usePromoViewEvent } from 'common/hooks';
import { Section } from '../section';
import { SectionSubTitle } from '../section-subtitle';
import { Typography } from '../typography';
import { productCardsSectionTextWrapper } from './product-cards-section-styles';
import { ProductCardsList, ProductCardStructure } from '../product-cards-list';

type ProductCardsSectionProps<T> = {
  elementBlock: string;
  productList: T[];
  renderProductCard: (product: T) => React.ReactNode;
  title: string;
  swiperSlideClassName?: string;
  description?: string;
};

export const ProductCardsSection = <T extends ProductCardStructure>({
  elementBlock,
  productList,
  renderProductCard,
  title,
  swiperSlideClassName,
  description,
}: ProductCardsSectionProps<T>) => {
  const productRefs = useRef<HTMLDivElement[]>([]);

  const promotions = useMemo(
    () =>
      productList.map(({ name }, index: number) => ({
        name: `${elementBlock} / ${title}`,
        creative: name,
        position: (index + 1).toString(),
      })),
    [productList, elementBlock, title]
  );

  usePromoViewEvent(productRefs, `${elementBlock} / ${title}`, promotions);

  return (
    <Section>
      <div className={productCardsSectionTextWrapper}>
        <SectionSubTitle adaptiveMargin margin="s">
          {title}
        </SectionSubTitle>
        {description && (
          <Typography adaptive={false} variant="m" size="body_long">
            {description}
          </Typography>
        )}
      </div>
      <ProductCardsList
        elementBlock={elementBlock}
        productList={productList}
        renderProductCard={renderProductCard}
        sectionTitle={title}
        swiperSlideClassName={swiperSlideClassName}
      />
    </Section>
  );
};

ProductCardsSection.defaultProps = {
  swiperSlideClassName: '',
  description: '',
};
